import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, PaperPlaneRight, ArrowClockwise, ChatCircleDots } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_QUESTIONS = [
  'Which seller has sold the most items?',
  'What is our total revenue and commission so far?',
  'How many items are live on marketplace right now?',
  'Which items have been listed the longest without selling?',
  'What was the last item we sold and for how much?',
  'Which category sells best?',
];

const Intel = () => {
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem('listers_intel_session') || ''
  );
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/intel/history`, {
      params: { session_id: sessionId },
      withCredentials: true,
    })
      .then((res) => setMessages(res.data || []))
      .catch(() => { /* fresh start is fine */ });
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || sending) return;
    setSending(true);
    setMessages((prev) => [...prev, {
      role: 'user',
      content: q,
      created_at: new Date().toISOString(),
    }]);
    setQuestion('');
    try {
      const res = await axios.post(`${API}/intel/ask`, {
        question: q,
        session_id: sessionId || undefined,
      }, { withCredentials: true });
      const { answer, session_id } = res.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        localStorage.setItem('listers_intel_session', session_id);
      }
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: answer,
        created_at: new Date().toISOString(),
      }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Assistant unavailable — try again shortly.';
      toast.error(msg);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Sorry — ${msg}`,
        created_at: new Date().toISOString(),
        error: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try {
        await axios.post(`${API}/intel/reset`, { question: 'reset', session_id: sessionId },
          { withCredentials: true });
      } catch (err) {
        // Reset is best-effort — local clear still happens below.
        console.debug('Intel reset request failed (non-fatal):', err?.message);
      }
    }
    localStorage.removeItem('listers_intel_session');
    setSessionId('');
    setMessages([]);
    toast.success('Conversation cleared');
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="intel-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-listers-green/20 flex items-center justify-center">
              <Sparkle className="w-5 h-5 text-listers-green" weight="fill" />
            </div>
            <h1 className="text-3xl font-heading font-semibold text-[#474747]">Listers Intel</h1>
          </div>
          <p className="text-sm text-gray-500 ml-13">
            Ask questions about your sellers, items, sales and payouts — answers use your live data.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-semibold text-gray-500 hover:text-[#474747] flex items-center gap-1.5"
          data-testid="intel-reset-btn"
        >
          <ArrowClockwise className="w-3.5 h-3.5" /> New chat
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E9E9E9] flex flex-col min-h-[60vh]">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="intel-messages">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <ChatCircleDots className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-5">Try one of these to start</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                {SUGGESTED_QUESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    disabled={sending}
                    className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-[#E9E9E9] bg-gray-50 hover:bg-listers-green/10 hover:border-listers-green/40 text-[#474747] transition-colors disabled:opacity-50"
                    data-testid="intel-suggested-question"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div
              key={`${m.created_at || 'msg'}-${idx}-${m.role}`}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={
                  m.role === 'user'
                    ? 'max-w-[85%] sm:max-w-[70%] bg-listers-green/20 text-[#474747] rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap'
                    : `max-w-[85%] sm:max-w-[70%] ${m.error ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-[#474747]'} rounded-2xl rounded-bl-md px-4 py-2.5 text-sm whitespace-pre-wrap`
                }
                data-testid={m.role === 'user' ? 'intel-user-message' : 'intel-assistant-message'}
              >
                {m.content}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-gray-500">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.15s' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.3s' }} />
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="border-t border-[#E9E9E9] p-3 sm:p-4 flex gap-2"
        >
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about sellers, items, revenue…"
            className="flex-1 px-4 py-2.5 rounded-full border border-[#E9E9E9] focus:outline-none focus:border-listers-green text-sm"
            disabled={sending}
            data-testid="intel-question-input"
          />
          <button
            type="submit"
            disabled={sending || !question.trim()}
            className="px-4 py-2.5 rounded-full bg-listers-green hover:bg-listers-green/90 text-[#474747] font-semibold text-sm disabled:opacity-50 flex items-center gap-1.5"
            data-testid="intel-send-btn"
          >
            <PaperPlaneRight className="w-4 h-4" weight="fill" />
            <span className="hidden sm:inline">Ask</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Intel;
